import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import GetAppTwoToneIcon from "@mui/icons-material/GetAppTwoTone";
import SaveIcon from "@mui/icons-material/Save";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import { Divider, Tooltip } from "@mui/material";
import { BackdropOpen } from "App";
import { Methods } from "enums/Methods";
import { NewTemplateNameDialog } from "features/WorklistTools/shared/components/NewTemplateNameDialog";
import {
  StyledIconButton,
  StyledToolbarSection,
} from "features/WorklistTools/shared/components/styles/styled-components";
import { TwoOptionDialog } from "features/WorklistTools/shared/components/TwoOptionDialog";
import { UploadWorklist } from "features/WorklistTools/shared/components/UploadWorklist";
import { WarningDialog } from "features/WorklistTools/shared/components/WarningDialog";
import { ViewWorklistTemplate } from "features/WorklistTools/shared/components/WorklistTemplates/ViewWorklistTemplates";
import { WorklistToolWarnings } from "features/WorklistTools/shared/components/WorklistToolWarnings";
import {
  WorklistLastUpdatedDate,
  WorklistTools,
} from "features/WorklistTools/shared/interfaces";
import { useWorklistToolStateProvider } from "features/WorklistTools/shared/state/StateProvider";
import { fillSourcePlateWithColors } from "features/WorklistTools/shared/WorklistHelpers";
import {
  useDeleteExistingMethodReservationMutation,
  useGetExistingMethodReservationLazyQuery,
  useOverwriteExistingWorklistTemplateMutation,
  useSaveWorklistToolTemplateMutation,
  WorklistToolTemplate,
  WorklistToolTemplateContentsInput,
} from "graphql/generated/schema-types";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import React, { useEffect, useState } from "react";
import { ConfirmDialog } from "shared-components/ModalsAndDialogs/ConfirmDialog";
import { Alert, AlertType } from "shared-components/toast";
import {
  isBatchPlatesCandidate,
  sourcePlateIsNotColSorted,
} from "../ExternalThawToolHelpers";
import { DestPlateInfoState, ExternalThawToolActions } from "../state";
import { ExternalThawInternalAction } from "../state/actions";
import { useGenerateWorklistClick } from "../state/handlers";
import { useExternalThawStateProvider } from "../state/StateProvider";
import { getExtThawRequiredWorklistWarnings } from "../warnings";
import {
  parseTemplateMethodSettings,
  parseTemplateWellMappings,
} from "./toolbarhandlers/HandleOpenTemplate";
import {
  buildAdditionalMethodSettingsTemplate,
  buildDeadTotalPlateTemplate,
  buildExternalThawTubeTemplate,
  buildInt1ToDestTemplate,
  buildIntCentrifugationTemplate,
  buildIntermediatWellsTemplate,
  buildSeedingStrategyTemplate,
  getDeadPlateInfo,
  getDestPlateInfo,
  getIntPlateInfo,
  getSourcePlateInfo,
} from "./toolbarhandlers/HandleSaveTemplate";
import {
  parseUploadedWorklist,
  UploadedWorklist,
} from "./toolbarhandlers/HandleUploadWorklist";

export const GeneralOptions = () => {
  const dispatch = useAppReduxDispatch();
  const { worklistToolInternalState } = useWorklistToolStateProvider();
  const { externalThawInternalState, externalThawDispatch } =
    useExternalThawStateProvider();
  const [requiredWarnings, setRequiredWarnings] = useState([""]);
  const [twoOptionDialogOpen, setTwoOptionDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [uploadWorklistOpen, setUploadWorklistOpen] = useState(false);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [batchDestPlatesDialogOpen, setBatchDestPlatesDialogOpen] =
    useState(false);
  const [saveWorklistTemplate] = useSaveWorklistToolTemplateMutation();
  const externalThawToolInfo = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present,
  );

  const [overwriteExistingWorklistToolTemplate] =
    useOverwriteExistingWorklistTemplateMutation({
      onCompleted() {
        Alert({
          type: AlertType.SUCCESS,
          message: "Template Saved",
        });
      },
    });

  const [getExistingMethodReservation, { data: methodReservation }] =
    useGetExistingMethodReservationLazyQuery({
      fetchPolicy: "network-only",
      onCompleted() {
        if (sourcePlateAlreadyReserved) {
          Alert({
            type: AlertType.WARNING,
            message:
              "Source plate has already been reserved. You must delete the reservation if you would like to generate a worklist with this plate.",
          });
        } else if (
          sourcePlateIsNotColSorted(externalThawToolInfo.sourcePlateInfo)
        ) {
          Alert({
            type: AlertType.WARNING,
            message:
              "Source plate is not sorted by column. Upload sorted source plate to continue.",
          });
        }
      },
    });

  const [deleteMethodReservation] =
    useDeleteExistingMethodReservationMutation();

  const sourcePlateAlreadyReserved =
    (methodReservation?.existingMethodReservation?.length ?? 0) > 0;

  useEffect(() => {
    getExistingMethodReservation({
      variables: {
        where: {
          isCompleted: { eq: false },
          methodReservationsPlateBarcodes: {
            some: {
              plateBarcode: {
                in: externalThawToolInfo.sourcePlateInfo.map(
                  (item) => item.plateBarcode,
                ),
              },
            },
          },
        },
      },
    });
  }, [externalThawToolInfo.sourcePlateInfo]);

  const handleDeleteMethodReservationClick = () => {
    deleteMethodReservation({
      variables: {
        methodReservationId: methodReservation?.existingMethodReservation
          ? methodReservation!.existingMethodReservation[0]!.methodReservationId
          : 0,
      },
    }).then((res) => {
      getExistingMethodReservation({
        variables: {
          where: {
            isCompleted: { eq: false },
            methodReservationsPlateBarcodes: {
              some: {
                plateBarcode: {
                  eq: externalThawToolInfo.sourcePlateInfo[0].plateBarcode,
                },
              },
            },
          },
        },
      });
    });
  };

  const allPlateTypesSelected = () => {
    const hasSourcePlateType = externalThawToolInfo.sourcePlateInfo.some(
      (plate) => plate.labwareTypeCode !== "",
    );
    const hasDestPlateType = externalThawToolInfo.destPlateInfo.some(
      (plate) => plate.labwareTypeCode !== "",
    );
    const hasIntPlateType = externalThawToolInfo.intPlateInfo.some(
      (plate) => plate.labwareTypeCode !== "",
    );
    const hasDeadPlateType = externalThawToolInfo.deadPlateType !== "";

    return (
      hasSourcePlateType &&
      hasDestPlateType &&
      hasIntPlateType &&
      hasDeadPlateType
    );
  };

  const handleSaveTemplateClose = (
    saveTemplate: boolean,
    templateName: string,
  ) => {
    setSaveTemplateDialogOpen(false);
    if (!saveTemplate) return;

    if (!allPlateTypesSelected()) {
      Alert({
        type: AlertType.ERROR,
        message:
          "Cannot save template until Source, Dest, Int, and Dead plate types are selected.",
      });
      return;
    }

    saveWorklistTemplate({
      variables: {
        input: getTemplateInfoToSave(undefined, templateName),
      },
    }).then((data) => {
      Alert({
        type: AlertType.SUCCESS,
        message: "Template Saved",
      });
    });
  };

  const [overwriteTemplateMode, setOverwriteTemplateMode] = useState(false);
  const handleTwoOptionDialogClose = (rightOption: boolean) => {
    setTwoOptionDialogOpen(false);
    if (rightOption) {
      setTemplateDialogOpen(true);
      setOverwriteTemplateMode(true);
      return;
    }
    setSaveTemplateDialogOpen(true);
  };

  const [uploadingWorklist, setUploadingWorklist] = useState(false);
  useEffect(() => {
    BackdropOpen({
      open: uploadingWorklist,
      header: "Uploading Worklist",
      subText: "Do not reload page",
      size: 60,
    });
  }, [uploadingWorklist]);

  const handleUploadWorklistClose = async (
    uploadWorklist: boolean,
    worklistRows: UploadedWorklist[],
  ) => {
    setUploadWorklistOpen(false);
    if (!uploadWorklist) return;
    setUploadingWorklist(true);
    const parsedWorklistInfo = await parseUploadedWorklist(worklistRows);
    externalThawDispatch({
      type: ExternalThawInternalAction.SET_STATE,
      payload: {
        key: "uploadedWorklist",
        value: true,
      },
    });
    dispatch(
      ExternalThawToolActions.UPLOAD_SOURCE_INT_DEAD_AND_DEST_PLATE_INFO({
        sourcePlateInfo: parsedWorklistInfo.sourcePlateInfo,
        intPlateInfo: parsedWorklistInfo.intPlateInfo,
        destPlateInfo: parsedWorklistInfo.destPlateInfo,
        deadPlateBarcode: parsedWorklistInfo.deadPlateBarcode,
        deadPlateType: parsedWorklistInfo.deadPlateType,
      }),
    );
    dispatch(
      ExternalThawToolActions.UPLOAD_WORKLIST_VALUES({
        harvestWells: parsedWorklistInfo.harvestWells,
        int1ToDest: parsedWorklistInfo.int1ToDest,
      }),
    );

    externalThawDispatch({
      type: ExternalThawInternalAction.UPDATE_METHOD_SETTINGS_BY_TEMPLATE,
      payload: {
        methodSettings: parsedWorklistInfo.methodSettings,
      },
    });
    setUploadingWorklist(false);
  };

  const checkForWarnings = async () => {
    const requiredWarning = await getExtThawRequiredWorklistWarnings(
      externalThawToolInfo.sourcePlateInfo,
      externalThawToolInfo.destPlateInfo,
      externalThawToolInfo.intPlateInfo,
      externalThawToolInfo.deadPlateType,
      externalThawToolInfo.worklistValues,
      externalThawInternalState,
      worklistToolInternalState,
    );
    setRequiredWarnings(requiredWarning);
  };

  const generateWorklist = useGenerateWorklistClick();

  const batchDestPlatesCandidate = isBatchPlatesCandidate(
    externalThawToolInfo.destPlateInfo,
  );

  const handleGenerateWorklistClick = () => {
    if (batchDestPlatesCandidate) {
      setBatchDestPlatesDialogOpen(true);
    } else if (requiredWarnings.length < 1) {
      generateWorklist({ batchDestPlates: false });
    }
  };

  const handleBatchPlatesDialogClose = async (continueAction: boolean) => {
    setBatchDestPlatesDialogOpen(false);
    if (continueAction && requiredWarnings.length < 1) {
      generateWorklist({ batchDestPlates: true });
      return;
    }
    generateWorklist({ batchDestPlates: false });
    return;
  };

  const handleOverwriteTemplateClose = (
    overwriteTemplate: boolean,
    templateInfo: WorklistToolTemplate | undefined,
  ) => {
    setTemplateDialogOpen(false);
    if (!overwriteTemplate) {
      setOverwriteTemplateMode(false);
      return;
    }

    if (!allPlateTypesSelected()) {
      Alert({
        type: AlertType.ERROR,
        message:
          "Cannot save template until Source, Dest, Int, and Dead plate types are selected.",
      });
      return;
    }

    overwriteExistingWorklistToolTemplate({
      variables: {
        input: getTemplateInfoToSave(templateInfo!.worklistToolTemplateId),
      },
    });

    return;
  };

  const getTemplateInfoToSave = (
    worklistToolTemplateId?: number,
    templateName?: string,
  ) => {
    const worklistValues = externalThawToolInfo.worklistValues;
    const sourcePlateState = externalThawToolInfo.sourcePlateInfo;
    const intPlateState = externalThawToolInfo.intPlateInfo;
    const destPlateState = externalThawToolInfo.destPlateInfo;
    const deadPlateBarcode = externalThawToolInfo.deadPlateBarcode;
    const deadPlateType = externalThawToolInfo.deadPlateType;

    const externalThawTubes = buildExternalThawTubeTemplate(sourcePlateState);

    const intermediatePlateMappings = buildIntermediatWellsTemplate(
      worklistValues.harvestWells,
      sourcePlateState,
      intPlateState,
    );

    const deadTotalPlate = buildDeadTotalPlateTemplate(
      externalThawInternalState.methodSettings,
      deadPlateType,
      deadPlateBarcode,
    );

    const seedingStrategy = buildSeedingStrategyTemplate(
      externalThawInternalState.methodSettings,
    );

    const destinationPlateMappings = buildInt1ToDestTemplate(
      worklistValues.int1ToDest,
      intPlateState,
      destPlateState,
    );

    const intSpinParams = buildIntCentrifugationTemplate(
      externalThawInternalState.methodSettings,
    );

    // Methodsettings not saved in the WL
    const additionalMethodSettings = buildAdditionalMethodSettingsTemplate(
      externalThawInternalState.methodSettings,
    );

    let worklist: WorklistToolTemplateContentsInput[] = [];

    worklist = [
      ...externalThawTubes,
      ...intermediatePlateMappings,
      deadTotalPlate,
      seedingStrategy,
      ...destinationPlateMappings,
      intSpinParams,
      ...additionalMethodSettings,
    ];

    const template = {
      worklistToolTemplateId,
      templateName,
      methodId: 148,
      sourcePlateInfo: getSourcePlateInfo(externalThawToolInfo),
      intPlateInfo: getIntPlateInfo(externalThawToolInfo),
      destPlateInfo: getDestPlateInfo(externalThawToolInfo),
      deadPlateInfo: getDeadPlateInfo(externalThawToolInfo),
      worklistToolTemplateContentsInput: worklist,
    };

    return template;
  };

  const handleTemplateClose = (
    loadTemplate: boolean,
    templateInfo: WorklistToolTemplate | undefined,
  ) => {
    setTemplateDialogOpen(false);
    if (!loadTemplate) return;
    const harvestWells = parseTemplateWellMappings(
      templateInfo?.worklistToolTemplateContents?.filter(
        (e) => e?.task === "IntermediatePlate",
      ) ?? [],
    );
    const int1ToDest = parseTemplateWellMappings(
      templateInfo?.worklistToolTemplateContents?.filter(
        (e) => e?.task === "DestinationPlate",
      ) ?? [],
    );

    const templateMethodSettings = parseTemplateMethodSettings(
      templateInfo?.worklistToolTemplateContents,
    );

    const sourcePlates = templateInfo?.worklistToolTemplatePlates
      ?.filter((e) => e?.plateType === "Source")
      .map((srcPlate) => {
        const rows = [...new Array(srcPlate?.numberOfRows)].map(
          (item, index) => index + 1,
        );
        const cols = [...new Array(srcPlate?.numberOfColumns)].map(
          (item, index) => index + 1,
        );
        return {
          plateBarcode: "",
          labwareTypeCode: srcPlate?.labwareTypeCode,
          operatingVol: srcPlate?.operatingVol ?? null,
          rows,
          cols,
          wellInfo: fillSourcePlateWithColors(rows, cols),
        };
      });

    const totalDeadAliquotVol =
      (templateMethodSettings?.numberOfAliquots ?? 0) *
      (templateMethodSettings?.aliquotVolume ?? 0);

    const intPlates = templateInfo?.worklistToolTemplatePlates
      ?.filter((e) => e?.plateType === "Int")
      .map((intPlate) => {
        return {
          plateBarcode: "",
          labwareTypeCode: intPlate?.labwareTypeCode,
          operatingVol: intPlate?.operatingVol ?? 0,
          resuspensionVol: intPlate?.resuspensionVol ?? 0,
          rows: [...new Array(intPlate?.numberOfRows)].map(
            (item, index) => index + 1,
          ),
          cols: [...new Array(intPlate?.numberOfColumns)].map(
            (item, index) => index + 1,
          ),
          intWellVol: intPlate?.resuspensionVol ?? 0 - totalDeadAliquotVol,
        };
      });

    const destPlates: DestPlateInfoState[] | undefined =
      templateInfo?.worklistToolTemplatePlates
        ?.filter((e) => e?.plateType === "Dest")
        .map((destPlate) => {
          return {
            index: destPlate!.plateIndex,
            plateBarcode: "",
            labwareTypeCode: destPlate?.labwareTypeCode ?? "",
            operatingVol: destPlate?.operatingVol ?? 0,
            preprocess: destPlate?.preprocess ?? false,
            topup: destPlate?.topup ?? false,
            minOperatingVol: 0,
            rows: [...new Array(destPlate?.numberOfRows)].map(
              (item, index) => index + 1,
            ),
            cols: [...new Array(destPlate?.numberOfColumns)].map(
              (item, index) => index + 1,
            ),
            topUpVol: destPlate?.topUpVol ?? 0,
            startsInIncubator: destPlate?.startsInIncubator ?? true,
            endsInIncubator: destPlate?.endsInIncubator ?? true,
          };
        });

    const deadPlateType =
      templateInfo?.worklistToolTemplatePlates
        ?.filter((e) => e?.plateType === "Dead")
        .map((item) => item?.labwareTypeCode)[0] ?? "";

    dispatch(
      ExternalThawToolActions.UPLOAD_PLATES_FROM_TEMPLATE({
        sourcePlates,
        intPlates,
        destPlates,
        deadPlateType,
      }),
    );

    setTimeout(() => {
      dispatch(
        ExternalThawToolActions.UPLOAD_WORKLIST_VALUES_FROM_TEMPLATE({
          harvestWells,
          int1ToDest,
        }),
      );
    }, 1000);

    externalThawDispatch({
      type: ExternalThawInternalAction.UPDATE_METHOD_SETTINGS_BY_TEMPLATE,
      payload: {
        methodSettings: templateMethodSettings,
      },
    });
  };
  return (
    <React.Fragment>
      <StyledToolbarSection>
        <Tooltip
          onOpen={checkForWarnings}
          title={
            sourcePlateAlreadyReserved ? (
              "Source Plate has already been reserved. Please delete the existing reservation."
            ) : sourcePlateIsNotColSorted(
                externalThawToolInfo.sourcePlateInfo,
              ) ? (
              "Source plate is not sorted by column. Upload sorted source plate to continue."
            ) : (
              <WorklistToolWarnings
                requiredWarnings={requiredWarnings}
                optionalWarnings={[]}
              />
            )
          }
          placement="right"
        >
          <div>
            <StyledIconButton
              onClick={handleGenerateWorklistClick}
              disabled={
                sourcePlateAlreadyReserved ||
                sourcePlateIsNotColSorted(externalThawToolInfo.sourcePlateInfo)
              }
            >
              <GetAppTwoToneIcon />
            </StyledIconButton>
          </div>
        </Tooltip>
        <StyledIconButton onClick={() => setUploadWorklistOpen(true)}>
          <Tooltip title="Upload Worklist" placement="right">
            <UploadOutlinedIcon />
          </Tooltip>
        </StyledIconButton>
        <StyledIconButton onClick={() => setTemplateDialogOpen(true)}>
          <Tooltip title="Open Template" placement="right">
            <FolderOpenIcon />
          </Tooltip>
        </StyledIconButton>
        <StyledIconButton onClick={() => setTwoOptionDialogOpen(true)}>
          <Tooltip title="Save Template" placement="right">
            <SaveIcon />
          </Tooltip>
        </StyledIconButton>
        <StyledIconButton
          disabled={
            methodReservation?.existingMethodReservation
              ? !(methodReservation?.existingMethodReservation?.length > 0)
              : true
          }
          onClick={() => handleDeleteMethodReservationClick()}
        >
          <Tooltip title="Delete Existing Reservation" placement="right">
            <DeleteForeverIcon />
          </Tooltip>
        </StyledIconButton>
      </StyledToolbarSection>
      <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />

      <WarningDialog
        title={"Batch Destination Plates"}
        message={[
          "The method can seed multiple destination plates with channels at the same time. Would you like to batch them?",
        ]}
        isOpen={batchDestPlatesDialogOpen}
        leftButtonText={"No thanks"}
        rightButtonText={"Yes, batch them"}
        handleClose={(continueAction) =>
          handleBatchPlatesDialogClose(continueAction)
        }
      />

      <UploadWorklist
        isOpen={uploadWorklistOpen}
        handleClose={(uploadWorklist, worklistRows) =>
          handleUploadWorklistClose(uploadWorklist, worklistRows)
        }
      />

      <TwoOptionDialog
        isOpen={twoOptionDialogOpen}
        handleClose={(rightButton) => handleTwoOptionDialogClose(rightButton)}
        title={"Do you want to overwrite an existing template?"}
        leftButtonText="No"
        rightButtonText="Yes"
      />

      <ConfirmDialog leftButtonText="No" rightButtonText="Yes" />

      <NewTemplateNameDialog
        isOpen={saveTemplateDialogOpen}
        handleClose={(saveTemplate, templateName) =>
          handleSaveTemplateClose(saveTemplate, templateName)
        }
      />

      <ViewWorklistTemplate
        isOpen={templateDialogOpen}
        handleClose={(loadTemplate, templateInfo) =>
          overwriteTemplateMode
            ? handleOverwriteTemplateClose(loadTemplate, templateInfo)
            : handleTemplateClose(loadTemplate, templateInfo)
        }
        overwriteTemplateMode={overwriteTemplateMode}
        methodId={Methods.External_Thaw}
        // Update WorklistLastUpdatedDate whenever outputted worklist is changed
        dateWorklistLastUpdated={
          new Date(WorklistLastUpdatedDate[WorklistTools.ExternalThaw])
        }
      />
    </React.Fragment>
  );
};
